import type { ReactNode } from 'react';
import { Box, Tooltip } from '@mui/material';
import { SIGNAL_LABELS, type EvidenceItem } from '@hv/shared';
import { SIGNAL_COLORS } from '../theme';

export interface Span {
  start: number;
  end: number;
  evidence: EvidenceItem;
}

function normalizedWithMap(text: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  let lastSpace = false;
  for (let i = 0; i < text.length; i++) {
    let ch = text[i]!;
    if (/\s/.test(ch)) {
      if (lastSpace) continue;
      ch = ' ';
      lastSpace = true;
    } else {
      lastSpace = false;
    }
    ch = ch.replace(/[‘’]/, "'").replace(/[“”]/, '"').toLowerCase();
    norm += ch;
    map.push(i);
  }
  return { norm, map };
}

export function findEvidenceSpans(text: string, evidence: EvidenceItem[]): Span[] {
  const { norm, map } = normalizedWithMap(text);
  const spans: Span[] = [];
  for (const ev of evidence) {
    const needle = normalizedWithMap(ev.quote.trim()).norm;
    if (!needle) continue;
    let from = 0;
    for (;;) {
      const idx = norm.indexOf(needle, from);
      if (idx < 0) break;
      const start = map[idx]!;
      const end = map[idx + needle.length - 1]! + 1;
      if (!spans.some((s) => start < s.end && end > s.start)) spans.push({ start, end, evidence: ev });
      from = idx + needle.length;
    }
  }
  return spans.sort((a, b) => a.start - b.start);
}

export function HighlightedText({ text, evidence }: { text: string; evidence: EvidenceItem[] }) {
  const spans = findEvidenceSpans(text, evidence);
  const out: ReactNode[] = [];
  let pos = 0;
  spans.forEach((s, i) => {
    if (s.start > pos) out.push(text.slice(pos, s.start));
    const c = SIGNAL_COLORS[s.evidence.signal];
    out.push(
      <Tooltip key={i} title={`Narrative thread: ${SIGNAL_LABELS[s.evidence.signal]}`}>
        <Box component="mark" data-signal={s.evidence.signal} sx={{ bgcolor: c.bg, color: 'inherit', borderBottom: `2px solid ${c.fg}`, px: 0.25, borderRadius: 0.5 }}>
          {text.slice(s.start, s.end)}
        </Box>
      </Tooltip>,
    );
    pos = s.end;
  });
  if (pos < text.length) out.push(text.slice(pos));
  return <>{out}</>;
}
